import React, { useState, useEffect, useRef } from 'react';
import s from './NovaWallet.module.sass';
import { useAppContext } from '../../../app-core/AppContext';
import { useNovaPortfolio } from '../useNovaPortfolio';
import BankLogo from '../components/BankLogo';
import FlagIcon from '../../../components/FlagIcon/FlagIcon';
import {
    Wallet as WalletIcon, PieChart, History as HistoryIcon,
    ArrowDownLeft, ArrowUpRight, TrendingUp, TrendingDown, ChevronRight,
    Landmark, Copy, Check, Repeat, CandlestickChart, Lock,
    ArrowDownToLine, LineChart, ShieldCheck,
} from 'lucide-react';
import binance from '../../../img/binance-coin-bnb-logo-CD94CC6D31-seeklogo.com.png';
import etherium from '../../../img/Ethereum-ETH-icon.png';
import bitcoin from '../../../img/Bitcoin.svg.png';
import tron from '../../../img/tron.png';
import usdclogo from '../../../img/usdclogo.png';

/**
 * NovaWallet — раздел «Кошелёк», полная переверстка оригинального Wallet
 * с сохранением всей логики:
 *  — главный баланс 0.00, а реальная сумма показана строкой статуса вывода,
 *    окрашенной по статусу Transfer-транзакции;
 *  — портфель: выбранная крипта с балансом из тарифа/кастомной суммы и
 *    процентом роста от депозита, остальные монеты нулевые;
 *  — таблица транзакций: клик по статусу циклически меняет его (админ-фишка),
 *    действие «Исправить сейчас»/«Нет», значок «!» и иконка банка поверх
 *    иконки транзакции, IBAN в описании переводов.
 */

const CRYPTO_ASSET_MAP = {
    BTC: { name: 'Bitcoin', icon: bitcoin },
    ETH: { name: 'Ethereum', icon: etherium },
    BNB: { name: 'Binance Coin', icon: binance },
    USDC: { name: 'USD Coin', icon: usdclogo },
};

const ALL_CRYPTO_ASSETS = [
    { name: 'Bitcoin', symbol: 'BTC', icon: bitcoin, change: '+0.0%', changePositive: false },
    { name: 'Ethereum', symbol: 'ETH', icon: etherium, change: '+0.0%', changePositive: false },
    { name: 'Binance Coin', symbol: 'BNB', icon: binance, change: '+3147.2%', changePositive: true },
    { name: 'USD Coin', symbol: 'USDC', icon: usdclogo, change: '+0.1%', changePositive: true },
    { name: 'USDT', symbol: 'USDT', icon: tron, change: '+0.1%', changePositive: true },
];

const STATUS_CYCLE = ['success', 'in process', 'error', 'pending', 'fixed'];

// «Вам может быть интересно» — CoinGecko trending, кэш на уровне модуля
// переживает перемонтирования при смене разделов
const TRENDING_API = 'https://api.coingecko.com/api/v3/search/trending';
const TRENDING_REFRESH_MS = 5 * 60 * 1000;
const TRENDING_MAX = 6;
let trendingCache = { items: null, fetchedAt: 0 };

export default function NovaWallet() {
    const {
        siteSettings = {},
        maxTotalMoney,
        customMaxMoney,
        customDepositMoney,
        currentTariffLabel,
        calculateCryptoAmount,
        getCryptoAmount,
        selectedCrypto,
        transactions = [],
        setTransactions,
        cryptoAdress,
        imgCoin,
        network,
        depositMoneyHistory,
        nameUser,
        copyToClipboard,
        setShowModaWithdrawal,
        safeT,
    } = useAppContext();

    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;
    const currencyFormat = siteSettings.currencyFormat || 'USD';

    // Единая «каноническая» сумма портфеля — ровно та же, что показывает дашборд
    // (useNovaPortfolio). В режиме отзыва это сгенерированный выигрыш
    // (verifyModalAmount), а не базовая сумма тарифа (maxTotalMoney). Все числовые
    // поля кошелька считаем от неё, чтобы верх, портфель, крипта и транзакции
    // сходились между собой и с дашбордом.
    const { displayAmount: portfolioAmount } = useNovaPortfolio();

    const [selectedTimeFilter, setSelectedTimeFilter] = useState('24H');
    const [selectedTxFilter, setSelectedTxFilter] = useState('all');
    const [copiedKey, setCopiedKey] = useState('');
    const [interestCoins, setInterestCoins] = useState(trendingCache.items);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const res = await fetch(TRENDING_API);
                if (!res.ok) return;
                const json = await res.json();
                const list = (json.coins || []).slice(0, TRENDING_MAX).map(({ item }) => ({
                    id: item.id,
                    name: item.name,
                    symbol: (item.symbol || '').toUpperCase(),
                    icon: item.small || item.thumb,
                    rank: item.market_cap_rank,
                    change24h: item.data?.price_change_percentage_24h?.usd ?? null,
                }));
                if (!list.length) return;
                trendingCache = { items: list, fetchedAt: Date.now() };
                if (!cancelled) setInterestCoins(list);
            } catch (_) { /* сеть недоступна — остаёмся на кэше */ }
        };
        if (!trendingCache.items || Date.now() - trendingCache.fetchedAt > TRENDING_REFRESH_MS) load();
        const timer = setInterval(load, TRENDING_REFRESH_MS);
        return () => { cancelled = true; clearInterval(timer); };
    }, []);

    // Форматирование чисел по настройке конфига (как в оригинале)
    const formatNumber = (number, format = siteSettings.numberFormat || 'comma') => {
        if (isNaN(number) || number === null || number === undefined) return '0';
        const parts = parseFloat(number).toFixed(2).split('.');
        const [integerPart, decimalPart] = parts;
        if (format === 'dot') {
            return `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimalPart}`;
        }
        return `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimalPart}`;
    };

    const formatCrypto = (number, decimalPlaces = 5) => {
        if (isNaN(number) || number === null || number === undefined) return '0.' + '0'.repeat(decimalPlaces);
        return parseFloat(number).toFixed(decimalPlaces);
    };

    // ── Статус активной транзакции вывода — окрашивает строку под балансом
    const getWithdrawalStatus = () => {
        if (!transactions || transactions.length === 0) return 'pending';
        const withdrawalTransaction = transactions.find(tx =>
            tx.type === `Transfer ${currencyFormat}` || tx.type === 'Transfer RM' || tx.type === 'Pengeluaran'
        );
        if (!withdrawalTransaction) return 'pending';
        return withdrawalTransaction.status.toLowerCase();
    };
    const withdrawalStatus = getWithdrawalStatus();

    const withdrawalTextMap = {
        error: t('wallet.withdrawalStatus.failed', 'вывод не удался - требуется действие'),
        pending: t('wallet.withdrawalStatus.pending', 'в процессе вывода'),
        'in process': t('wallet.withdrawalStatus.inProcess', 'вывод в процессе'),
        success: t('wallet.withdrawalStatus.success', 'вывод успешно завершён'),
    };
    const withdrawalText = withdrawalTextMap[withdrawalStatus] || withdrawalTextMap.pending;

    // Баланс «на удержании»: есть транзакция вывода с любым статусом кроме success —
    // деньги есть, но недоступны, пока вывод не завершён
    const hasWithdrawalTx = (transactions || []).some(tx =>
        tx.type === `Transfer ${currencyFormat}` || tx.type === 'Transfer RM' || tx.type === 'Pengeluaran'
    );
    const balanceOnHold = hasWithdrawalTx
        && withdrawalStatus !== 'success'
        && Number(portfolioAmount) > 0;

    // Копейки в сумме — отдельно, мельче и приподняты (как на дэшборде);
    // разделитель зависит от numberFormat, поэтому матчим и точку, и запятую
    const splitCents = (str) => {
        const m = String(str ?? '').match(/^(.*)[.,](\d{2})$/);
        return m ? [m[1], m[2]] : [String(str ?? ''), null];
    };
    const [balanceInt, balanceCents] = splitCents(balanceOnHold ? formatNumber(portfolioAmount) : '0.00');
    const withdrawalToneClass = {
        error: s.toneError,
        pending: s.tonePending,
        'in process': s.toneProcess,
        success: s.toneSuccess,
    }[withdrawalStatus] || s.tonePending;

    // ── Портфель (логика оригинала)
    const hasTariffSelected = Number(portfolioAmount) > 0;

    const getCryptoBalance = () => {
        if (customMaxMoney && calculateCryptoAmount) {
            const customAmount = parseFloat(customMaxMoney);
            if (customAmount > 0) {
                const cryptoAmount = calculateCryptoAmount(customAmount, selectedCrypto);
                return formatCrypto(parseFloat(cryptoAmount), siteSettings?.cryptoDecimalPlaces || 5);
            }
        }
        if (siteSettings?.otzovikMode && calculateCryptoAmount && Number(portfolioAmount) > 0) {
            const cryptoAmount = calculateCryptoAmount(portfolioAmount, selectedCrypto);
            return formatCrypto(parseFloat(cryptoAmount), siteSettings?.cryptoDecimalPlaces || 5);
        } else if (hasTariffSelected && getCryptoAmount) {
            return getCryptoAmount(currentTariffLabel || '💰 1 ТАРИФ').split(' ')[0];
        }
        return formatCrypto(0, siteSettings?.cryptoDecimalPlaces || 5);
    };

    const calculatePortfolioPercentage = () => {
        const currentBalance = parseFloat(String(portfolioAmount).replace(/,/g, ''));
        if (currentBalance === 0 || isNaN(currentBalance)) {
            return { value: '0.0%', positive: null };
        }
        let initialInvestment;
        if (customDepositMoney) {
            const customDeposit = parseFloat(customDepositMoney);
            if (customDeposit > 0 && !isNaN(customDeposit)) {
                initialInvestment = customDeposit;
            }
        }
        if (!initialInvestment) {
            const tariffDepositAmounts = siteSettings?.tariffDepositAmounts || {
                '💰 1 ТАРИФ': 169,
                '❗❗ 2 ТАРИФ': 299,
                '❗❗❗ 3 ТАРИФ': 429,
                '❗❗❗❗ 4 ТАРИФ': 849,
                '⚠️ VIP ТАРИФ': 1899,
            };
            initialInvestment = tariffDepositAmounts[currentTariffLabel || '💰 1 ТАРИФ'] || 169;
        }
        if (!initialInvestment || initialInvestment === 0) {
            return { value: '0.0%', positive: null };
        }
        const growthPercentage = ((currentBalance - initialInvestment) / initialInvestment * 100);
        const isPositive = growthPercentage >= 0;
        return { value: `${isPositive ? '+' : ''}${growthPercentage.toFixed(1)}%`, positive: isPositive };
    };
    const portfolioPercentage = calculatePortfolioPercentage();

    // ── «Счёт верифицирован»: клик по щиту у счёта зажигает зелёное
    // подтверждение на 15 секунд, затем гаснет. Любое изменение данных
    // карточки сбрасывает статус сразу — подтверждение относилось к ним
    const [acctVerified, setAcctVerified] = useState(false);
    const acctVerifyTimer = useRef(null);

    // Тумблер: повторный клик гасит сразу, не дожидаясь таймера
    const handleAcctVerify = () => {
        clearTimeout(acctVerifyTimer.current);
        if (acctVerified) {
            setAcctVerified(false);
            return;
        }
        setAcctVerified(true);
        acctVerifyTimer.current = setTimeout(() => setAcctVerified(false), 15000);
    };

    useEffect(() => {
        clearTimeout(acctVerifyTimer.current);
        setAcctVerified(false);
    }, [cryptoAdress, network, imgCoin?.img, depositMoneyHistory, maxTotalMoney, currencyFormat]);

    useEffect(() => () => clearTimeout(acctVerifyTimer.current), []);

    // Описание с лицензией («Брокер: Имя | Лицензия № 212847») рендерим
    // не плоской строкой, а как в хедере: текст + чип лицензии с зелёным
    // щитом «верифицирован»
    const renderTxDescription = (description) => {
        const str = String(description || '');
        const parts = str.split('|');
        if (parts.length !== 2) return str;
        return (
            <>
                {parts[0].trim()}
                <span className={s.rowLicChip}>
                    <ShieldCheck size={11} strokeWidth={2.2} aria-hidden="true" />
                    {parts[1].trim()}
                </span>
            </>
        );
    };

    // Средняя обрезка адресов: 0xfre...90Ce
    const shortAddr = (addr, head = 5, tail = 4) => {
        const str = String(addr || '');
        if (str.length <= head + tail + 3) return str;
        return `${str.slice(0, head)}...${str.slice(-tail)}`;
    };

    const handleCopy = (key, text) => {
        if (!text) return;
        if (typeof copyToClipboard === 'function') {
            copyToClipboard(text);
        } else if (navigator.clipboard) {
            navigator.clipboard.writeText(text).catch(() => {});
        }
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(''), 1600);
    };

    // ── Спарклайн для карточек портфеля: детерминированное случайное блуждание.
    // Сид включает данные из админки (тариф, имя, крипта, суммы), поэтому любое
    // изменение перегенерирует линии — как график на дэшборде. При 0% изменения
    // рисуем ровную прямую: движения не было
    const sparkSalt = [portfolioAmount, customMaxMoney, customDepositMoney, currentTariffLabel, nameUser, selectedCrypto].join('|');
    const buildSpark = (seed, positive) => {
        const W = 104, H = 36, P = 2;
        if (positive !== true && positive !== false) {
            // Прямая по центру с градиентом под ней: график есть, движения нет
            const y = (H / 2).toFixed(1);
            const line = `M${P} ${y} L${W - P} ${y}`;
            const area = `${line} L${W - P} ${H} L${P} ${H} Z`;
            return { line, area };
        }
        let h = 2166136261;
        for (const ch of String(seed)) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); }
        const rnd = () => {
            h = Math.imul(h ^ (h >>> 15), 2246822507);
            h = Math.imul(h ^ (h >>> 13), 3266489909);
            return ((h ^= h >>> 16) >>> 0) / 4294967296;
        };
        const N = 22;
        let v = 0.5;
        let walk = [];
        for (let i = 0; i < N; i++) {
            v += (rnd() - 0.5) * 0.34;
            v = Math.min(0.95, Math.max(0.05, v));
            walk.push(v);
        }
        // Нормализуем блуждание в 0..1 — это «характер» пиков
        const wMin = Math.min(...walk), wMax = Math.max(...walk), wRange = wMax - wMin || 1;
        // Тренд = линейный рост + пики, затухающие к краям (огибающая-синус).
        // Старт всегда глобальный минимум, финиш всегда глобальный максимум:
        // середина физически не может выйти за конечную точку
        const pts = walk.map((w, i) => {
            const ramp = i / (N - 1);
            const wiggle = ((w - wMin) / wRange - 0.5) * 0.55 * Math.sin(Math.PI * ramp);
            const y = ramp + wiggle;
            return positive ? y : 1 - y;
        });
        const min = Math.min(...pts), max = Math.max(...pts), range = max - min || 1;
        const coords = pts.map((p, i) => [
            P + (i / (N - 1)) * (W - P * 2),
            P + (1 - (p - min) / range) * (H - P * 2),
        ]);
        // Сглаживание Catmull-Rom → кубические Безье: плавная линия без углов
        const line = coords.reduce((acc, p2, i, arr) => {
            if (i === 0) return `M${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
            const p0 = arr[i - 2] || arr[i - 1];
            const p1 = arr[i - 1];
            const p3 = arr[i + 1] || p2;
            const c1x = p1[0] + (p2[0] - p0[0]) / 6;
            const c1y = p1[1] + (p2[1] - p0[1]) / 6;
            const c2x = p2[0] - (p3[0] - p1[0]) / 6;
            const c2y = p2[1] - (p3[1] - p1[1]) / 6;
            return `${acc} C${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
        }, '');
        const area = `${line} L${(W - P).toFixed(1)} ${H} L${P} ${H} Z`;
        return { line, area };
    };

    const otherCryptos = ALL_CRYPTO_ASSETS.filter(c => c.symbol !== selectedCrypto);
    const portfolioAssets = [
        {
            name: CRYPTO_ASSET_MAP[selectedCrypto]?.name || 'Ethereum',
            symbol: selectedCrypto || 'ETH',
            icon: CRYPTO_ASSET_MAP[selectedCrypto]?.icon || etherium,
            balance: getCryptoBalance(),
            value: hasTariffSelected ? `${currencyFormat} ${formatNumber(portfolioAmount)}` : `${currencyFormat} 0.00`,
            change: hasTariffSelected ? portfolioPercentage.value : '0.0%',
            changePositive: hasTariffSelected ? portfolioPercentage.positive : null,
        },
        ...otherCryptos.slice(0, 3).map(crypto => ({
            name: crypto.name,
            symbol: crypto.symbol,
            icon: crypto.icon,
            balance: formatCrypto(0, siteSettings?.cryptoDecimalPlaces || 5),
            value: `${currencyFormat} 0.00`,
            change: '0.0%',
            changePositive: null,
        })),
    ];

    // ── Транзакции
    const translateTransactionType = (type) => {
        if (type === 'Sesi Dagangan' || type?.trim() === 'Sesi Dagangan') {
            return t('wallet.transactionTypes.tradingSession', 'Торговая сессия');
        }
        if (type.startsWith('Transfer ')) {
            return `${t('wallet.transactionTypes.transfer', 'Перевод')} ${type.replace('Transfer ', '')}`;
        }
        if (type.startsWith('Penukaran ')) {
            return `${t('wallet.transactionTypes.conversion', 'Конвертация')} ${type.replace('Penukaran ', '')}`;
        }
        if (type.startsWith('Deposit ')) {
            return `${t('wallet.transactionTypes.deposit', 'Депозит')} ${type.replace('Deposit ', '')}`;
        }
        return type;
    };

    const translateStatus = (status) => {
        switch (status.toLowerCase()) {
            case 'success': return t('wallet.statuses.success', 'Успешно');
            case 'pending': return t('wallet.statuses.pending', 'Ожидание');
            case 'in process': return t('wallet.statuses.inProcess', 'В процессе');
            case 'error': return t('wallet.statuses.error', 'Ошибка');
            case 'fixed': return t('wallet.statuses.fixed', 'Исправлено');
            default: return status;
        }
    };

    const statusClass = (status) => ({
        success: s.stSuccess,
        'in process': s.stProcess,
        error: s.stError,
        pending: s.stPending,
        fixed: s.stFixed,
    }[status.toLowerCase()] || s.stPending);

    // Сплошные цвета для статусного значка «!» поверх иконки
    const alertClass = (status) => ({
        error: s.alertError,
        pending: s.alertPending,
        'in process': s.alertProcess,
    }[status] || s.alertPending);

    // Клик по статусу циклически меняет его (админ-фишка из оригинала)
    const cycleStatus = (transaction) => {
        const currentIndex = STATUS_CYCLE.indexOf(transaction.status.toLowerCase());
        const newStatus = STATUS_CYCLE[(currentIndex + 1) % STATUS_CYCLE.length];
        setTransactions(prev => prev.map(trans => {
            if (trans.id !== transaction.id) return trans;
            const updated = { ...trans, status: newStatus };
            updated.action = (newStatus === 'success' || newStatus === 'fixed')
                ? t('wallet.actions.none', 'Нет')
                : t('wallet.actions.fixNow', 'Исправить сейчас');
            return updated;
        }));
    };

    // Действие: переключение «Исправить сейчас»/«Нет»
    const toggleAction = (transaction) => {
        const isLocked = ['success', 'in process'].includes(transaction.status.toLowerCase());
        if (isLocked) return;
        const fixNow = t('wallet.actions.fixNow', 'Исправить сейчас');
        const none = t('wallet.actions.none', 'Нет');
        const current = transaction.action === 'None' ? none : (transaction.action === 'Fix now' ? fixNow : transaction.action);
        const newAction = current === fixNow ? none : fixNow;
        setTransactions(prev => prev.map(trans =>
            trans.id === transaction.id ? { ...trans, action: newAction } : trans
        ));
    };

    const renderAction = (transaction) => {
        const st = transaction.status.toLowerCase();
        const isLocked = st === 'success' || st === 'in process';
        const label = isLocked
            ? t('wallet.actions.none', 'Нет')
            : (transaction.action === 'None'
                ? t('wallet.actions.none', 'Нет')
                : (transaction.action === 'Fix now'
                    ? t('wallet.actions.fixNow', 'Исправить сейчас')
                    : transaction.action));
        const isFix = label === t('wallet.actions.fixNow', 'Исправить сейчас');
        return (
            <button
                type="button"
                className={`${s.actionBtn} ${isFix ? s.actionFix : ''}`}
                onClick={() => toggleAction(transaction)}
                disabled={isLocked}
            >
                {label}
            </button>
        );
    };

    const txFilters = [
        ['all', t('wallet.filters.allTransactions', 'Все транзакции')],
        ['withdrawals', t('wallet.filters.withdrawals', 'Выводы')],
        ['deposits', t('wallet.filters.deposits', 'Депозиты')],
        ['conversions', t('wallet.filters.conversions', 'Конвертации')],
    ];
    const timeFilters = ['1H', '24H', '1W', '1M', '1Y'];

    const isTransfer = (type) => type && (
        type.includes('Transfer') || type.includes('transfer') ||
        type.includes('Перевод') || type.includes('перевод')
    );

    // Иконка типа транзакции — из lucide-пака, на цветной плашке
    const txIconMeta = (type) => {
        if (isTransfer(type)) return { Icon: ArrowUpRight, cls: s.txAccent };
        if (type?.startsWith('Deposit ')) return { Icon: ArrowDownLeft, cls: s.txGood };
        if (type?.startsWith('Penukaran ')) return { Icon: Repeat, cls: s.txViolet };
        if (type === 'Sesi Dagangan' || type?.trim() === 'Sesi Dagangan') return { Icon: CandlestickChart, cls: s.txNeutral };
        return { Icon: Repeat, cls: s.txNeutral };
    };

    return (
        <div className={s.page}>
            <div className={s.top}>
                {/* ── Общий баланс */}
                <section className={s.balanceWrap} aria-label={t('wallet.sections.totalBalance', 'Общий баланс')}>
                    <span className={s.tab}>
                        <WalletIcon size={12} strokeWidth={2} aria-hidden="true" />
                        {t('wallet.sections.totalBalance', 'Общий баланс')}
                    </span>
                    <div className={s.balanceCard}>
                        <div className={s.balanceHead}>
                            <div className={s.balanceValue}>
                                <span className={s.balanceCurrency}>{currencyFormat}</span>
                                <span className={`${s.balanceAmount} ${balanceOnHold ? s.balanceAmountHold : ''}`}>
                                    {balanceInt}
                                    {balanceCents !== null && (
                                        <span className={s.balanceCents}>{balanceCents}</span>
                                    )}
                                </span>
                            </div>
                            <FlagIcon siteSettings={siteSettings} className={s.balanceFlag} alt="" />
                        </div>
                        {/* Бейдж статуса вывода — только если есть Transfer-транзакция;
                            окрашен по её статусу, при удержании баланса вместо точки — замок */}
                        {hasWithdrawalTx && (
                            <div className={s.withdrawalRow}>
                                <div className={`${s.withdrawalBadge} ${withdrawalToneClass}`}>
                                    {balanceOnHold
                                        ? <Lock size={12} strokeWidth={2.2} className={s.withdrawalIcon} aria-hidden="true" />
                                        : <span className={s.withdrawalDot} aria-hidden="true" />}
                                    <span className={s.withdrawalText}>
                                        <b>{currencyFormat} {formatNumber(portfolioAmount)}</b> {withdrawalText}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Детали счёта: депозит, рост, банковский счёт, криптокошелёк */}
                        <div className={s.detailList}>
                            <div className={s.detailRow}>
                                <span className={s.detailLabel}>
                                    <ArrowDownToLine size={13} strokeWidth={2} aria-hidden="true" />
                                    {t('dashboard.deposit', 'Депозит')}
                                </span>
                                <span className={s.detailValue}>{currencyFormat} {formatNumber(depositMoneyHistory || 0)}</span>
                            </div>
                            <div className={s.detailRow}>
                                <span className={s.detailLabel}>
                                    <LineChart size={13} strokeWidth={2} aria-hidden="true" />
                                    {t('dashboard.portfolioGrowth', 'Рост Портфолио')}
                                </span>
                                <span className={`${s.detailBadge} ${portfolioPercentage.positive === true ? s.chUp : portfolioPercentage.positive === false ? s.chDown : s.chFlat}`}>
                                    {portfolioPercentage.positive === true && <TrendingUp size={11} strokeWidth={2.2} aria-hidden="true" />}
                                    {portfolioPercentage.positive === false && <TrendingDown size={11} strokeWidth={2.2} aria-hidden="true" />}
                                    {hasTariffSelected ? portfolioPercentage.value : '0.0%'}
                                </span>
                            </div>
                            {cryptoAdress && (
                                <div className={s.detailRow}>
                                    <span className={s.detailLabel}>
                                        {/* Щит-верификатор: клик — зелёное «счёт верифицирован»
                                            на 15 секунд, гаснет само и при смене данных */}
                                        <button
                                            type="button"
                                            className={`${s.acctVerifyBtn} ${acctVerified ? s.acctVerifyOn : ''}`}
                                            onClick={handleAcctVerify}
                                            aria-pressed={acctVerified}
                                        >
                                            {/* Щит визуально «легче» стрелок при равном номинале —
                                                компенсируем размером; размер продублирован в CSS кнопки */}
                                            <ShieldCheck size={18} strokeWidth={2.2} aria-hidden="true" />
                                        </button>
                                        {siteSettings?.bankAccountDisplayName || 'IBAN/BIC'}
                                    </span>
                                    <span className={s.detailAccount}>
                                        {imgCoin?.img
                                            ? <img src={imgCoin.img} alt={network || ''} className={s.detailBankIcon} />
                                            : <Landmark size={13} strokeWidth={2} className={s.detailBankFallback} aria-hidden="true" />}
                                        <code className={s.detailMono}>{shortAddr(cryptoAdress, 12, 5)}</code>
                                        <button
                                            type="button"
                                            className={s.copyBtn}
                                            onClick={() => handleCopy('bank', cryptoAdress)}
                                            aria-label={t('settings.address.copy', 'Копировать')}
                                        >
                                            {copiedKey === 'bank'
                                                ? <Check size={12} strokeWidth={2.4} className={s.copyDone} aria-hidden="true" />
                                                : <Copy size={12} strokeWidth={2} aria-hidden="true" />}
                                        </button>
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className={s.balanceActions}>
                            <button type="button" className={s.depositBtn}>
                                <ArrowDownLeft size={15} strokeWidth={2.2} aria-hidden="true" />
                                {t('wallet.buttons.deposit', 'Депозит')}
                            </button>
                            <button type="button" className={s.withdrawBtn} onClick={() => setShowModaWithdrawal?.(true)}>
                                <ArrowUpRight size={15} strokeWidth={2.2} aria-hidden="true" />
                                {t('wallet.buttons.withdraw', 'Вывести')}
                            </button>
                        </div>
                    </div>
                </section>

                {/* ── Портфель */}
                <section className={s.portfolioWrap} aria-label={t('wallet.sections.yourPortfolio', 'Ваш портфель')}>
                    <span className={`${s.tab} ${s.tabRight}`}>
                        {t('wallet.sections.yourPortfolio', 'Ваш портфель')}
                        <PieChart size={12} strokeWidth={2} aria-hidden="true" />
                    </span>
                    <div className={s.portfolioCard}>
                        {/* Слева — карточки монет 2×2 на всю высоту */}
                        <div className={s.portfolioGrid}>
                            {portfolioAssets.map((asset, index) => (
                                <div key={index} className={`${s.assetCard} ${index === 0 ? s.assetPrimary : ''}`}>
                                    <div className={s.assetHead}>
                                        <img src={asset.icon} alt="" className={s.assetIcon} />
                                        <div className={s.assetMeta}>
                                            <span className={s.assetName}>{asset.name}</span>
                                            <span className={s.assetSymbol}>{asset.symbol}</span>
                                        </div>
                                        <span className={`${s.assetChange} ${asset.changePositive === true ? s.chUp : asset.changePositive === false ? s.chDown : s.chFlat}`}>
                                            {asset.changePositive === true && <TrendingUp size={11} strokeWidth={2.2} aria-hidden="true" />}
                                            {asset.changePositive === false && <TrendingDown size={11} strokeWidth={2.2} aria-hidden="true" />}
                                            {asset.change}
                                        </span>
                                    </div>
                                    <div className={s.assetNums}>
                                        <div className={s.assetNumsCol}>
                                            <span className={s.assetBalance}>{asset.balance} <i>{asset.symbol}</i></span>
                                            <span className={s.assetValue}>{asset.value}</span>
                                        </div>
                                        {(() => {
                                            const spark = buildSpark(`${asset.symbol}|${sparkSalt}`, asset.changePositive);
                                            const gradId = `walletSpark-${asset.symbol}`;
                                            const toneCls = asset.changePositive === true
                                                ? s.sparkUp
                                                : asset.changePositive === false ? s.sparkDown : s.sparkFlat;
                                            return (
                                                <svg
                                                    className={`${s.assetSpark} ${toneCls}`}
                                                    viewBox="0 0 104 36"
                                                    preserveAspectRatio="none"
                                                    aria-hidden="true"
                                                >
                                                    <defs>
                                                        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
                                                            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                                                        </linearGradient>
                                                    </defs>
                                                    {spark.area && <path d={spark.area} fill={`url(#${gradId})`} />}
                                                    <path
                                                        d={spark.line}
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        vectorEffect="non-scaling-stroke"
                                                    />
                                                </svg>
                                            );
                                        })()}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Справа — вертикальная колонка «Может быть интересно» */}
                        {interestCoins && interestCoins.length > 0 && (
                            <aside className={s.interest}>
                                <span className={s.interestTitle}>
                                    {t('wallet.sections.mayInterest', 'Вам может быть интересно')}
                                </span>
                                <div className={s.interestList}>
                                    {interestCoins.map((coin) => {
                                        const positive = coin.change24h === null ? null : coin.change24h >= 0;
                                        return (
                                            <div key={coin.id} className={s.interestItem}>
                                                <img src={coin.icon} alt="" className={s.interestIcon} loading="lazy" />
                                                <span className={s.interestNames}>
                                                    <span className={s.interestSymbol}>{coin.symbol}</span>
                                                    <span className={s.interestName}>{coin.name}</span>
                                                </span>
                                                {positive !== null && (
                                                    <span className={`${s.interestChange} ${positive ? s.chUp : s.chDown}`}>
                                                        {positive ? '↑' : '↓'} {Math.abs(coin.change24h).toFixed(1)}%
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </aside>
                        )}
                    </div>
                </section>
            </div>

            {/* ── История транзакций */}
            <section className={s.historyWrap} aria-label={t('wallet.sections.transactionHistory', 'История транзакций')}>
                <span className={s.tab}>
                    <HistoryIcon size={12} strokeWidth={2} aria-hidden="true" />
                    {t('wallet.sections.transactionHistory', 'История транзакций')}
                </span>
                <div className={s.historyCard}>
                    <div className={s.historyHead}>
                        <div className={s.txFilters}>
                            {txFilters.map(([key, label]) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={`${s.filterBtn} ${selectedTxFilter === key ? s.filterActive : ''}`}
                                    onClick={() => setSelectedTxFilter(key)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <div className={s.timeFilters}>
                            {timeFilters.map((filter) => (
                                <button
                                    key={filter}
                                    type="button"
                                    className={`${s.timeBtn} ${selectedTimeFilter === filter ? s.timeActive : ''}`}
                                    onClick={() => setSelectedTimeFilter(filter)}
                                >
                                    {filter}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={s.table}>
                        <div className={s.tableHead}>
                            <span>{t('wallet.tableHeaders.description', 'Описание')}</span>
                            <span>{t('wallet.tableHeaders.currency', 'Валюта')}</span>
                            <span>{t('wallet.tableHeaders.amount', 'Сумма')}</span>
                            <span>{t('wallet.tableHeaders.status', 'Статус')}</span>
                            <span className={s.cellRight}>{t('wallet.tableHeaders.action', 'Действие')}</span>
                        </div>
                        {transactions.map((transaction) => {
                            const st = transaction.status.toLowerCase();
                            const showAlert = st === 'error' || st === 'in process' || st === 'pending';
                            // Для переводов главный круг — лого банка, в углу — иконка отправки;
                            // остальные типы — иконка из lucide-пака на цветной плашке
                            const isBankTransfer = Boolean(imgCoin && imgCoin.img && isTransfer(transaction.type));
                            const { Icon: TxIcon, cls: txCls } = txIconMeta(transaction.type);
                            return (
                                <div key={transaction.id} className={s.row}>
                                    <div className={s.rowDesc}>
                                        <div className={s.rowIconWrap}>
                                            {isBankTransfer ? (
                                                <span className={s.bankPlate}>
                                                    <BankLogo src={imgCoin.img} alt={network || ''} />
                                                </span>
                                            ) : (
                                                <span className={`${s.rowIconPlate} ${txCls}`}>
                                                    <TxIcon size={17} strokeWidth={2.1} aria-hidden="true" />
                                                </span>
                                            )}
                                            {showAlert && (
                                                <span className={`${s.alertDot} ${alertClass(st)}`}>!</span>
                                            )}
                                            {isBankTransfer && (
                                                <span className={s.sendOverlay}>
                                                    <ArrowUpRight size={11} strokeWidth={2.6} aria-hidden="true" />
                                                </span>
                                            )}
                                        </div>
                                        <div className={s.rowMeta}>
                                            <span className={s.rowType}>{translateTransactionType(transaction.type)}</span>
                                            <span className={s.rowSub}>
                                                {(transaction.type === 'Transfer RM' || transaction.type === `Transfer ${currencyFormat}`)
                                                    ? `${siteSettings?.bankAccountDisplayName || 'IBAN/BIC'}: ${cryptoAdress}`
                                                    : renderTxDescription(transaction.description)}
                                            </span>
                                        </div>
                                    </div>
                                    <span className={s.rowCurrency}>
                                        {transaction.type === `Penukaran ${selectedCrypto}`
                                            ? `${selectedCrypto} → ${currencyFormat}`
                                            : currencyFormat}
                                    </span>
                                    <div className={s.rowAmount}>
                                        <span className={s.amountMain}>
                                            {transaction.amount.includes(currencyFormat)
                                                ? transaction.amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                                : transaction.amount}
                                        </span>
                                        <span className={s.amountSub}>
                                            {transaction.type.includes('Deposit') ? '+' : '-'}
                                            {transaction.type === 'Transfer RM' || transaction.type === `Transfer ${currencyFormat}`
                                                ? `${currencyFormat} ${formatNumber(portfolioAmount)}`
                                                : (transaction.amount.includes(currencyFormat)
                                                    ? transaction.amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                                    : transaction.amount)}
                                        </span>
                                    </div>
                                    <span>
                                        <button
                                            type="button"
                                            className={`${s.statusBadge} ${statusClass(transaction.status)}`}
                                            onClick={() => cycleStatus(transaction)}
                                        >
                                            {translateStatus(transaction.status)}
                                        </button>
                                    </span>
                                    <span className={s.cellRight}>{renderAction(transaction)}</span>
                                </div>
                            );
                        })}
                    </div>

                    <div className={s.historyFoot}>
                        <span className={s.footInfo}>
                            {transactions.length} {t('wallet.pagination.transactions', 'Транзакций')}
                        </span>
                        <button type="button" className={s.pageBtn} aria-current="page">1</button>
                        <button type="button" className={s.footSelect}>
                            {t('wallet.pagination.all', 'Все')}
                            <ChevronRight size={12} strokeWidth={2} className={s.footChevron} aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}
